import {AxiosError} from "axios";
import {makeAutoObservable, observable} from "mobx";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IUserStore} from "data/stores/user/user.store";
import type {IRoundsStore} from "data/stores/rounds/rounds.store";
import type {IConferencesStore} from "data/stores/conferences/conferences.store";
import type {IDivisionsStore} from "data/stores/divisions/divisions.store";
import type {ITournamentsStore} from "data/stores/tournaments/tournaments.store";
import type {ISquadsStore} from "data/stores/squads/squads.store";
import type {IStandingsStore} from "data/stores/standings/standings.store";
import type {ILightPredictionsStore} from "data/stores/light_predictions/light_predictions.store";
import type {IModalsStore} from "data/stores/modals/modals.store";
import {ModalType} from "data/enums";
import {extractErrorMessage} from "data/utils";
import {IApiResponse} from "data/services/http";
import {type IWelcomeModalStore} from "data/stores/welcome_modal/welcome_modal.store";
import type {IShareStore} from "data/stores/share/share.store";
import {ConnextraType, createConnextraScript} from "data/utils/createConnextraScript";
import {useNavigate} from "react-router-dom";
import {OFF_SEASON_PATH} from "data/constants";

interface IProps {
	navigate: ReturnType<typeof useNavigate>;
}

export interface ILightPredictorPageController extends ViewController<IProps> {
	readonly i18n: ILocalizationStore;
	isLoading: boolean;

	get isOffSeason(): boolean;
}

@injectable()
export class LightPredictorPageController implements ILightPredictorPageController {
	@observable isLoading: boolean = true;
	@observable private _navigate?: ReturnType<typeof useNavigate>;

	get isOffSeason() {
		return this._roundStore.isOffSeason;
	}

	constructor(
		@inject(Bindings.LocalizationStore) readonly i18n: ILocalizationStore,
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.RoundsStore) private _roundStore: IRoundsStore,
		@inject(Bindings.ConferencesStore) private _conferenceStore: IConferencesStore,
		@inject(Bindings.DivisionsStore) private _divisionStore: IDivisionsStore,
		@inject(Bindings.TournamentsStore) private _tournamentStore: ITournamentsStore,
		@inject(Bindings.SquadsStore) private _squadStore: ISquadsStore,
		@inject(Bindings.StandingsStore) private _standingsStore: IStandingsStore,
		@inject(Bindings.LightPredictionsStore) private _lpStore: ILightPredictionsStore,
		@inject(Bindings.ModalsStore) private _modalStore: IModalsStore,
		@inject(Bindings.WelcomeModalStore) private _welcomeModalStore: IWelcomeModalStore,
		@inject(Bindings.ShareStore) private _shareStore: IShareStore
	) {
		makeAutoObservable(this);
	}

	private setIsLoading(value: boolean): void {
		this.isLoading = value;
	}

	private async getJSONData() {
		return Promise.all([
			this._roundStore.fetchRounds(),
			this._squadStore.fetchSquads(),
			this._conferenceStore.fetchConferences(),
			this._divisionStore.fetchDivisions(),
			this._tournamentStore.fetchTournaments(),
			this._standingsStore.fetchStandings(),
		]);
	}

	dispose() {
		this._lpStore.clearPredictionState();
		this._modalStore.hideModal();
	}

	async init(param: IProps) {
		this._navigate = param.navigate;

		const isFinalSaved = Boolean(JSON.parse(localStorage.getItem("final_save") || "false"));

		try {
			await this.getJSONData();

			if (this._userStore.isAuthorized) {
				await this._lpStore.getPredictions();
				await this._lpStore.savePredictionsFromLocalStorage();
			}

			if (isFinalSaved) {
				void this._shareStore.sharePredictionImage();
				this._modalStore.showModal(ModalType.SHARE_PICKS, {
					message: "",
				});

				localStorage.removeItem("final_save");
			}

			this.showWelcomeModal();
		} catch (error) {
			this._modalStore.showModal(ModalType.ERROR, {
				message: extractErrorMessage(error as AxiosError<IApiResponse>),
			});
		} finally {
			if (this._roundStore.isOffSeason) {
				this._navigate?.(OFF_SEASON_PATH, {replace: true});
			}

			createConnextraScript(ConnextraType.LP_PAGE, this._userStore.user?.id);
			this.setIsLoading(false);
		}
	}

	private showWelcomeModal = () => {
		if (!this._lpStore.isPredictionsSaved && !this.isOffSeason) {
			this._welcomeModalStore.showModal();
		}
	};
}
